import { Controller } from "@hotwired/stimulus"

// The displayed image is already the fully-framed, branded raster — Images::Watermark bakes the
// white margin, border, and logo into watermarked_image. So print and download just hand that PNG
// to the browser: no canvas compositing, no logo overlay, no DPI math.
export default class extends Controller {
  static values = { imageUrl: String, title: String, eventsUrl: String }

  async print(event) {
    event.preventDefault()
    if (!this.imageUrlValue) return
    this.#track("print")

    // Match the page orientation to the image so a landscape page doesn't letterbox on portrait paper.
    let orientation = "portrait"
    try {
      const img = await this.#loadImage(this.imageUrlValue)
      orientation = img.naturalWidth > img.naturalHeight ? "landscape" : "portrait"
    } catch {
      // dimensions unknown — fall back to portrait
    }

    const win = window.open("", "_blank")
    if (!win) return // pop-up blocked
    this.#buildPrintDocument(win, orientation)
  }

  async download(event) {
    event.preventDefault()
    if (!this.imageUrlValue) return
    this.#track("download")

    try {
      // Cross-origin CDN URL: the anchor `download` attribute is ignored cross-origin, so fetch
      // the bytes and download an object URL instead. (CDN sends Access-Control-Allow-Origin.)
      const response = await fetch(this.imageUrlValue, { mode: "cors" })
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = this.#filename()
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      // CORS/network failure — open directly so the user can save manually
      window.open(this.imageUrlValue, "_blank")
    }
  }

  async share(event) {
    event.preventDefault()
    const url = window.location.href
    this.#track("share")

    if (navigator.share) {
      try { await navigator.share({ title: this.titleValue, url }) } catch {}
      return
    }

    this.#copyUrl(url, event.currentTarget)
  }

  // Fire-and-forget usage-metrics beacon (ColoringPages::EventsController).
  // These actions never touch the server otherwise, so counting happens here.
  #track(eventType) {
    if (!this.eventsUrlValue) return
    const data = new FormData()
    data.append("event_type", eventType)
    if (navigator.sendBeacon) {
      navigator.sendBeacon(this.eventsUrlValue, data)
    } else {
      fetch(this.eventsUrlValue, { method: "POST", body: data, keepalive: true }).catch(() => {})
    }
  }

  #copyUrl(url, btn) {
    const showFeedback = () => {
      const textNode = btn.lastChild
      const original = textNode.textContent
      textNode.textContent = " Copied!"
      setTimeout(() => { textNode.textContent = original }, 2000)
    }

    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(showFeedback).catch(() => this.#execCopy(url, showFeedback))
    } else {
      this.#execCopy(url, showFeedback)
    }
  }

  #execCopy(url, onSuccess) {
    const el = document.createElement("textarea")
    el.value = url
    el.style.cssText = "position:fixed;opacity:0;pointer-events:none"
    document.body.appendChild(el)
    el.focus()
    el.select()
    try { document.execCommand("copy"); onSuccess() } catch {}
    document.body.removeChild(el)
  }

  #loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = reject
      img.src = src
    })
  }

  #filename() {
    return `${this.titleValue.toLowerCase().replace(/\s+/g, "-")}.png`
  }

  // Full-bleed print of the already-framed image — its baked white margin is the print safe-area.
  // The popup inherits the site origin and titles are LLM-authored from user prompts, so the
  // document is built with DOM APIs — never string-interpolated HTML — to keep markup in a
  // title from ever being parsed (see issue #228).
  #buildPrintDocument(win, orientation) {
    const doc = win.document

    doc.title = this.titleValue

    const style = doc.createElement("style")
    style.textContent = `
      @page { size: ${orientation}; margin: 0; }
      html, body { margin: 0; height: 100%; }
      img { display: block; width: 100%; height: 100%; object-fit: contain; }
    `
    doc.head.appendChild(style)

    const img = doc.createElement("img")
    img.alt = this.titleValue
    const printWhenReady = () => {
      win.focus()
      win.print()
    }
    img.addEventListener("load", printWhenReady)
    img.addEventListener("error", printWhenReady)
    img.src = this.imageUrlValue
    doc.body.appendChild(img)
  }
}
